import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth, useNotificationsSocket } from "../lib/auth";
import { ArrowLeft, Send, BadgeCheck, Loader2, Play } from "lucide-react";

function timeStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function SharePreview({ share }) {
  if (!share) return null;
  if (share.missing) {
    return (
      <div className="mt-2 border border-dashed border-border rounded-xl p-3 text-xs text-muted-foreground">
        Shared {share.type} was removed.
      </div>
    );
  }
  const to = share.type === "debate" ? `/debates/${share.id}` : `/thoughts`;
  return (
    <Link to={to} className="mt-2 flex items-center gap-3 border border-border rounded-xl p-2 bg-background hover:bg-muted transition-colors">
      <div className="w-14 h-14 rounded-lg bg-black/90 relative shrink-0 overflow-hidden">
        {share.thumbnail_url ? (
          <img src={fileUrl(share.thumbnail_url)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[hsl(var(--primary-soft))]" />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <Play className="w-5 h-5 text-white fill-white" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] label-mono text-muted-foreground uppercase">{share.type}</div>
        <div className="text-sm font-semibold line-clamp-2">{share.title}</div>
        {share.author_username && (
          <div className="text-[11px] text-muted-foreground">@{share.author_username}</div>
        )}
      </div>
    </Link>
  );
}

export default function ChatThread() {
  const { peer_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = () => {
    api.get(`/chats/with/${peer_id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { setLoading(true); load(); }, [peer_id]);

  // Autoscroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data]);

  // Realtime updates for this thread
  useNotificationsSocket((n) => {
    if (n.kind === "message" && n.meta?.sender_id === peer_id) load();
  });

  const send = async (e) => {
    e?.preventDefault?.();
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/chats/with/${peer_id}/message`, { text: value });
      setText("");
      setData((d) => d ? { ...d, messages: [...d.messages, r.data.message] } : d);
    } catch { /* noop */ }
    finally { setSending(false); }
  };

  if (loading) return (
    <div className="p-10 flex items-center justify-center text-muted-foreground text-sm">
      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
    </div>
  );
  if (!data) return <div className="p-10 text-center text-sm text-muted-foreground">Chat not found.</div>;

  const peer = data.peer;

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate("/chats")}
            className="p-2 rounded-full hover:bg-muted lg:hidden"
            data-testid="chat-back"
            aria-label="Back to chats"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Link to={`/profile/${peer.username}`} className="flex items-center gap-3 flex-1 min-w-0 group">
            <div className="w-10 h-10 rounded-full bg-muted overflow-hidden flex items-center justify-center">
              {peer.profile_photo ? (
                <img src={fileUrl(peer.profile_photo)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-bold text-[hsl(var(--primary))]">{peer.username?.[0]?.toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold flex items-center gap-1 group-hover:text-[hsl(var(--primary))]">
                @{peer.username}
                {peer.verification_status === "verified" && (
                  <BadgeCheck className="w-3.5 h-3.5 fill-[hsl(var(--primary))] text-white" />
                )}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{peer.full_name}</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
        <div className="px-4 py-6 space-y-2">
          {data.messages.length === 0 && (
            <div className="text-center py-20 text-sm text-muted-foreground">
              Say hi to <span className="font-semibold">@{peer.username}</span> 👋
            </div>
          )}
          {data.messages.map((m, idx) => {
            const mine = m.sender_id === user.id;
            const prev = data.messages[idx - 1];
            const showAvatar = !mine && (!prev || prev.sender_id !== m.sender_id);
            return (
              <div key={m.id} className={`flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                {!mine && (
                  <div className={`w-7 h-7 rounded-full bg-muted overflow-hidden flex items-center justify-center shrink-0 ${showAvatar ? "opacity-100" : "opacity-0"}`}>
                    {peer.profile_photo ? (
                      <img src={fileUrl(peer.profile_photo)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[10px] font-bold text-[hsl(var(--primary))]">{peer.username?.[0]?.toUpperCase()}</span>
                    )}
                  </div>
                )}
                <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                  <div
                    className={`px-3.5 py-2 rounded-2xl text-sm break-words ${
                      mine
                        ? "bg-[hsl(var(--primary))] text-white rounded-br-md"
                        : "bg-muted text-foreground rounded-bl-md"
                    }`}
                  >
                    {m.text}
                    <SharePreview share={m.share_ref} />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 px-1">{timeStamp(m.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Composer */}
      <form onSubmit={send} className="bg-background/95 backdrop-blur border-t border-border">
        <div className="flex items-center gap-2 px-4 py-3">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Message…"
            className="flex-1 border border-border rounded-full px-4 py-2.5 text-sm bg-transparent outline-none focus:border-[hsl(var(--primary))]"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="w-10 h-10 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center disabled:opacity-40 transition-opacity"
            data-testid="chat-send"
            aria-label="Send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}
